import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import Table from '@mui/joy/Table';
import Checkbox from '@mui/joy/Checkbox';
import CircularProgress from '@mui/joy/CircularProgress';
import Chip from '@mui/joy/Chip';
import Button from '@mui/joy/Button';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import {
  fetchUsers,
  fetchOrganisations,
  updateUser,
  updateOrganisation,
  createOrganisation,
  deleteOrganisation,
} from '../../api/users-admin';
import { useMe } from '../../api/use-me';

const SEARCH_DEBOUNCE_MS = 400;

// Canonical RBAC roles (mirror lib/auth/permissions.js). The roles an admin may
// actually assign come from GET /me (me.assignableRoles); this list is the
// display/fallback superset.
const ALL_ROLES = ['owner', 'member', 'textOnly', 'audioOnly', 'support', 'orgAdmin', 'superAdmin'];
const STATUS_OPTIONS = ['provisional', 'active', 'suspended', 'deactivated'];
const NO_ROLE = '(none)';
const MODELS_HINT = 'Comma-separated modelName prefixes — e.g. "text:", "pipecat:ultravox", "livekit:openai-realtime/gpt-realtime", "*". Blank = no restriction.';

function statusColor(status) {
  switch (status) {
    case 'active': return 'success';
    case 'suspended': return 'warning';
    case 'deactivated': return 'danger';
    default: return 'neutral';
  }
}

// role is now a STRING (was a JSONB bag). Render it directly.
function roleSummary(role) {
  return typeof role === 'string' && role ? role : '';
}

function parseModels(str) {
  return String(str ?? '').split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
}
function sameModels(a, b) {
  // Order-insensitive: a prefix list is a set, so a pure reorder isn't a change.
  return JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());
}
function coerceLimit(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null; // a stray non-numeric entry => unlimited, never NaN
}

export default function UsersPanel() {
  const { me } = useMe();
  const isSuperAdmin = !!me?.isSuperAdmin;

  const [users, setUsers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterSearchDebounced, setFilterSearchDebounced] = useState('');
  const [orgSearch, setOrgSearch] = useState('');
  const [selectedOrgIds, setSelectedOrgIds] = useState(() => new Set());

  const [detailTarget, setDetailTarget] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Organisation edit/create modal (super-admin only).
  const [orgTarget, setOrgTarget] = useState(null); // a row, or { __new: true }
  const [orgEdits, setOrgEdits] = useState({});
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgSaveError, setOrgSaveError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setFilterSearchDebounced(filterSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filterSearch]);

  // Initial load of the org filter list. Users are loaded by the search effect.
  useEffect(() => {
    let cancelled = false;
    fetchOrganisations()
      .then((rows) => { if (!cancelled) setOrganisations(rows); })
      .catch((err) => { if (!cancelled) setLoadError(err?.message || 'Failed to load organisations'); });
    return () => { cancelled = true; };
  }, []);

  // Server-side user search via ?search= (iLike on name+email backend-side).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchUsers({ search: filterSearchDebounced || undefined })
      .then((rows) => { if (!cancelled) setUsers(rows); })
      .catch((err) => { if (!cancelled) setLoadError(err?.message || 'Failed to load users'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filterSearchDebounced]);

  const toggleOrgSelection = useCallback((orgId) => {
    setSelectedOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  }, []);

  const clearOrgSelection = useCallback(() => setSelectedOrgIds(new Set()), []);

  const visibleOrganisations = useMemo(() => {
    const q = orgSearch.trim().toLowerCase();
    if (!q) return organisations;
    return organisations.filter((o) => (o.name || '').toLowerCase().includes(q) || (o.id || '').toLowerCase().includes(q));
  }, [organisations, orgSearch]);

  const filteredUsers = useMemo(() => {
    if (selectedOrgIds.size === 0) return users;
    return users.filter((u) => u.organisationId && selectedOrgIds.has(u.organisationId));
  }, [users, selectedOrgIds]);

  // ── User edit ────────────────────────────────────────────────────────────
  const openDetail = useCallback((row) => {
    setDetailTarget(row);
    setEdits({});
    setSaveError(null);
  }, []);

  const closeDetail = useCallback(() => {
    if (saving) return;
    setDetailTarget(null);
    setEdits({});
    setSaveError(null);
  }, [saving]);

  // Roles the current admin may assign (from /me), always including the target's
  // current role so it stays visible even if this admin couldn't re-grant it.
  const roleOptions = useMemo(() => {
    const assignable = me?.assignableRoles?.length ? me.assignableRoles : ALL_ROLES;
    const set = new Set(assignable);
    if (detailTarget?.role) set.add(detailTarget.role);
    return [...set];
  }, [me, detailTarget]);

  const handleSave = useCallback(async () => {
    if (!detailTarget) return;
    const patch = {};
    if (edits.name !== undefined && edits.name !== detailTarget.name) patch.name = edits.name;
    if (edits.status !== undefined && edits.status !== detailTarget.status) patch.status = edits.status;
    if (edits.agentLimit !== undefined) {
      const coerced = coerceLimit(edits.agentLimit);
      if (coerced !== detailTarget.agentLimit) patch.agentLimit = coerced;
    }
    if (edits.role !== undefined && edits.role !== detailTarget.role) patch.role = edits.role;
    if (edits.allowedModels !== undefined) {
      const arr = parseModels(edits.allowedModels);
      if (!sameModels(arr, detailTarget.allowedModels)) patch.allowedModels = arr;
    }
    if (edits.organisationId !== undefined) {
      const next = edits.organisationId === '' ? null : edits.organisationId;
      if (next !== (detailTarget.organisationId ?? null)) patch.organisationId = next;
    }
    if (Object.keys(patch).length === 0) {
      closeDetail();
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateUser(detailTarget.id, patch);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
      setDetailTarget(null);
      setEdits({});
    } catch (err) {
      setSaveError(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [closeDetail, detailTarget, edits]);

  // ── Organisation edit / create (super-admin only) ──────────────────────────
  const openOrgEdit = useCallback((org) => {
    setOrgTarget(org);
    setOrgEdits({});
    setOrgSaveError(null);
  }, []);
  const openOrgCreate = useCallback(() => {
    setOrgTarget({ __new: true });
    setOrgEdits({ status: 'active' });
    setOrgSaveError(null);
  }, []);
  const closeOrgEdit = useCallback(() => {
    if (orgSaving) return;
    setOrgTarget(null);
    setOrgEdits({});
    setOrgSaveError(null);
  }, [orgSaving]);

  const handleOrgSave = useCallback(async () => {
    if (!orgTarget) return;
    const isNew = !!orgTarget.__new;
    setOrgSaving(true);
    setOrgSaveError(null);
    try {
      if (isNew) {
        const payload = {
          name: (orgEdits.name ?? '').trim(),
          status: orgEdits.status ?? 'active',
          agentLimit: coerceLimit(orgEdits.agentLimit),
          role: orgEdits.role && orgEdits.role !== NO_ROLE ? orgEdits.role : null,
          allowedModels: orgEdits.allowedModels !== undefined ? parseModels(orgEdits.allowedModels) : null,
        };
        if (!payload.name) { setOrgSaveError('A name is required.'); setOrgSaving(false); return; }
        const created = await createOrganisation(payload);
        setOrganisations((prev) => [created, ...prev]);
      } else {
        const patch = {};
        if (orgEdits.name !== undefined && orgEdits.name !== orgTarget.name) patch.name = orgEdits.name;
        if (orgEdits.status !== undefined && orgEdits.status !== orgTarget.status) patch.status = orgEdits.status;
        if (orgEdits.agentLimit !== undefined) {
          const coerced = coerceLimit(orgEdits.agentLimit);
          if (coerced !== orgTarget.agentLimit) patch.agentLimit = coerced;
        }
        if (orgEdits.role !== undefined) {
          const nextRole = orgEdits.role === NO_ROLE ? null : orgEdits.role;
          if (nextRole !== (orgTarget.role ?? null)) patch.role = nextRole;
        }
        if (orgEdits.allowedModels !== undefined) {
          const arr = parseModels(orgEdits.allowedModels);
          if (!sameModels(arr, orgTarget.allowedModels)) patch.allowedModels = arr;
        }
        if (Object.keys(patch).length === 0) { closeOrgEdit(); return; }
        const updated = await updateOrganisation(orgTarget.id, patch);
        setOrganisations((prev) => prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
      }
      setOrgTarget(null);
      setOrgEdits({});
    } catch (err) {
      setOrgSaveError(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to save');
    } finally {
      setOrgSaving(false);
    }
  }, [closeOrgEdit, orgTarget, orgEdits]);

  // Soft-delete (deactivate) an organisation — super-admin only. Reuses orgSaving
  // for the loading state (delete and save are mutually exclusive in the modal).
  const handleOrgDelete = useCallback(async () => {
    if (!orgTarget || orgTarget.__new) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Deactivate organisation "${orgTarget.name || orgTarget.id}"? Its members lose access until it is reactivated.`)) return;
    setOrgSaving(true);
    setOrgSaveError(null);
    try {
      await deleteOrganisation(orgTarget.id);
      setOrganisations((prev) => prev.map((o) => (o.id === orgTarget.id ? { ...o, status: 'deactivated' } : o)));
      setOrgTarget(null);
      setOrgEdits({});
    } catch (err) {
      setOrgSaveError(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to deactivate');
    } finally {
      setOrgSaving(false);
    }
  }, [orgTarget]);

  const orgField = (key, fallback) => (orgEdits[key] !== undefined ? orgEdits[key] : fallback);

  return (
    <Box sx={{ py: 2 }}>
      <Typography level="h4" sx={{ display: { xs: 'none', sm: 'block' } }}>
        Users
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: '320px minmax(0, 1fr)',
          gridTemplateColumns: '1fr',
          gap: 2,
          mt: 2,
          height: 'calc(100vh - 220px)',
          minHeight: 360,
        }}
      >
        <Box
          sx={{
            minHeight: 0,
            maxHeight: '320px',
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
            gap: 2,
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 'sm',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                User search
              </Typography>
              <Input
                size="sm"
                placeholder="Filter by name or email"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
            </Box>
            <Typography level="body2" color="neutral">
              Select organisations in the right panel to narrow the list.
            </Typography>
          </Sheet>

          <Sheet
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 'sm',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography level="title-sm" sx={{ flexGrow: 1 }}>
                Organisations
              </Typography>
              {selectedOrgIds.size > 0 && (
                <Button size="sm" variant="plain" color="neutral" onClick={clearOrgSelection}>
                  Unselect all
                </Button>
              )}
              {isSuperAdmin && (
                <Button size="sm" variant="soft" onClick={openOrgCreate}>
                  New organisation
                </Button>
              )}
              <Input
                size="sm"
                placeholder="Search organisations"
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                sx={{ maxWidth: 240 }}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table size="sm" stickyHeader sx={{ '--TableCell-paddingX': '0.75rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }} aria-label="Select" />
                    <th>Organisation</th>
                    {isSuperAdmin && <th style={{ width: 80 }} aria-label="Edit" />}
                  </tr>
                </thead>
                <tbody>
                  {visibleOrganisations.length === 0 && (
                    <tr>
                      <td colSpan={isSuperAdmin ? 3 : 2}>
                        <Typography level="body2" color="neutral">No organisations</Typography>
                      </td>
                    </tr>
                  )}
                  {visibleOrganisations.map((org) => (
                    <tr
                      key={org.id}
                      onClick={(e) => {
                        if (e.target.closest('input') || e.target.closest('button')) return;
                        toggleOrgSelection(org.id);
                      }}
                      style={{
                        cursor: 'pointer',
                        background: selectedOrgIds.has(org.id) ? 'var(--joy-palette-primary-softBg)' : undefined,
                      }}
                    >
                      <td>
                        <Checkbox
                          size="sm"
                          checked={selectedOrgIds.has(org.id)}
                          onChange={() => toggleOrgSelection(org.id)}
                          aria-label={`Select ${org.name || org.id}`}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{org.name || org.id}</td>
                      {isSuperAdmin && (
                        <td>
                          <Button
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={(e) => { e.stopPropagation(); openOrgEdit(org); }}
                          >
                            Edit
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>
          </Sheet>
        </Box>

        <Sheet
          variant="outlined"
          sx={{
            borderRadius: 'sm',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 0, flexShrink: 0, flexWrap: 'wrap' }}>
            <Typography level="title-sm" sx={{ flexGrow: 1 }}>
              Users
            </Typography>
            <Typography level="body2" color="neutral">
              {filteredUsers.length} shown
            </Typography>
          </Box>
          {loadError && (
            <Box sx={{ p: 2 }}>
              <Typography level="body2" color="danger">{loadError}</Typography>
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, pt: 1 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size="sm" />
              </Box>
            )}
            {!loading && (
              <Table stickyHeader size="sm" sx={{ '--TableCell-paddingX': '0.75rem' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Organisation</th>
                    <th style={{ width: '8%', whiteSpace: 'nowrap' }}>Agent limit</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <Typography level="body2" color="neutral">No users</Typography>
                      </td>
                    </tr>
                  )}
                  {filteredUsers.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => openDetail(row)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{row.name || '–'}</td>
                      <td>{row.email || '–'}</td>
                      <td>
                        <Chip size="sm" variant="soft" color={statusColor(row.status)}>
                          {row.status || 'unknown'}
                        </Chip>
                      </td>
                      <td>{row.Organisation?.name || (row.organisationId ? row.organisationId : '–')}</td>
                      <td>{row.agentLimit ?? '–'}</td>
                      <td>{roleSummary(row.role) || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Box>
        </Sheet>
      </Box>

      {/* ── User edit modal ── */}
      <Modal open={!!detailTarget} onClose={closeDetail}>
        <ModalDialog sx={{ minWidth: 420, maxWidth: 560 }}>
          <ModalClose />
          <Typography level="h4">Edit user</Typography>
          {detailTarget && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography level="body2" color="neutral">
                {detailTarget.email}
              </Typography>

              <FormControl size="sm">
                <FormLabel>Name</FormLabel>
                <Input
                  size="sm"
                  value={edits.name ?? detailTarget.name ?? ''}
                  onChange={(e) => setEdits((p) => ({ ...p, name: e.target.value }))}
                />
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Status</FormLabel>
                <Select
                  size="sm"
                  value={edits.status ?? detailTarget.status ?? 'provisional'}
                  onChange={(_, v) => setEdits((p) => ({ ...p, status: v }))}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <Option key={s} value={s}>{s}</Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Agent limit</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  placeholder="Unlimited"
                  value={edits.agentLimit ?? (detailTarget.agentLimit ?? '')}
                  onChange={(e) => setEdits((p) => ({ ...p, agentLimit: e.target.value }))}
                />
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Role</FormLabel>
                <Select
                  size="sm"
                  value={edits.role ?? detailTarget.role ?? roleOptions[0] ?? ''}
                  onChange={(_, v) => setEdits((p) => ({ ...p, role: v }))}
                >
                  {roleOptions.map((r) => (
                    <Option key={r} value={r}>{r}</Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Allowed models</FormLabel>
                <Input
                  size="sm"
                  placeholder="blank = no restriction"
                  value={edits.allowedModels ?? (detailTarget.allowedModels || []).join(', ')}
                  onChange={(e) => setEdits((p) => ({ ...p, allowedModels: e.target.value }))}
                />
                <FormHelperText>{MODELS_HINT}</FormHelperText>
              </FormControl>

              {isSuperAdmin && (
                <FormControl size="sm">
                  <FormLabel>Organisation</FormLabel>
                  <Select
                    size="sm"
                    value={edits.organisationId ?? detailTarget.organisationId ?? ''}
                    onChange={(_, v) => setEdits((p) => ({ ...p, organisationId: v }))}
                  >
                    <Option value="">(none)</Option>
                    {organisations.map((o) => (
                      <Option key={o.id} value={o.id}>{o.name || o.id}</Option>
                    ))}
                  </Select>
                  <FormHelperText>Move the user to another organisation (super admin only).</FormHelperText>
                </FormControl>
              )}

              {saveError && (
                <Typography level="body2" color="danger">{saveError}</Typography>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button size="sm" variant="plain" color="neutral" onClick={closeDetail} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" variant="solid" onClick={handleSave} loading={saving}>
                  Save
                </Button>
              </Box>
            </Box>
          )}
        </ModalDialog>
      </Modal>

      {/* ── Organisation edit / create modal (super-admin only) ── */}
      <Modal open={!!orgTarget} onClose={closeOrgEdit}>
        <ModalDialog sx={{ minWidth: 420, maxWidth: 560 }}>
          <ModalClose />
          <Typography level="h4">{orgTarget?.__new ? 'New organisation' : 'Edit organisation'}</Typography>
          {orgTarget && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {!orgTarget.__new && (
                <Typography level="body2" color="neutral">{orgTarget.id}</Typography>
              )}

              <FormControl size="sm">
                <FormLabel>Name</FormLabel>
                <Input
                  size="sm"
                  value={orgField('name', orgTarget.name ?? '')}
                  onChange={(e) => setOrgEdits((p) => ({ ...p, name: e.target.value }))}
                />
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Status</FormLabel>
                <Select
                  size="sm"
                  value={orgField('status', orgTarget.status ?? 'active')}
                  onChange={(_, v) => setOrgEdits((p) => ({ ...p, status: v }))}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <Option key={s} value={s}>{s}</Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Agent limit</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  placeholder="Unlimited"
                  value={orgField('agentLimit', orgTarget.agentLimit ?? '')}
                  onChange={(e) => setOrgEdits((p) => ({ ...p, agentLimit: e.target.value }))}
                />
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Baseline role</FormLabel>
                <Select
                  size="sm"
                  value={orgField('role', orgTarget.role ?? NO_ROLE)}
                  onChange={(_, v) => setOrgEdits((p) => ({ ...p, role: v }))}
                >
                  <Option value={NO_ROLE}>{NO_ROLE}</Option>
                  {ALL_ROLES.map((r) => (
                    <Option key={r} value={r}>{r}</Option>
                  ))}
                </Select>
                <FormHelperText>Inherited by every member of this org (RBAC floor).</FormHelperText>
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Allowed models (baseline)</FormLabel>
                <Input
                  size="sm"
                  placeholder="blank = no restriction"
                  value={orgField('allowedModels', (orgTarget.allowedModels || []).join(', '))}
                  onChange={(e) => setOrgEdits((p) => ({ ...p, allowedModels: e.target.value }))}
                />
                <FormHelperText>{MODELS_HINT}</FormHelperText>
              </FormControl>

              {orgSaveError && (
                <Typography level="body2" color="danger">{orgSaveError}</Typography>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  {!orgTarget.__new && isSuperAdmin && (
                    <Button size="sm" variant="soft" color="danger" onClick={handleOrgDelete} loading={orgSaving}>
                      Deactivate
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="sm" variant="plain" color="neutral" onClick={closeOrgEdit} disabled={orgSaving}>
                    Cancel
                  </Button>
                  <Button size="sm" variant="solid" onClick={handleOrgSave} loading={orgSaving}>
                    {orgTarget.__new ? 'Create' : 'Save'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
}
